import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import classNames from 'classnames';
import { useSSRSafeId as $b5e257d569688ac6$export$619500959fc48b26 } from '../../node_modules/@react-aria/ssr/dist/import.js';
import { FormHelperText } from '../Form/FormHelperText.js';
import '@livingdesign/tokens/dist/js/light/regular/components/Form';
import '@walmart-web/livingdesign-components/index.esm.css';
import '@walmart-web/livingdesign-components';
import '../Alert/Alert.js';
import '../Button/Button.js';
import '../Button/ButtonGroup.js';
import '../Radio/Radio.js';
import '../Tag/Tag.js';
import '../Chip/Chip.js';
import { applyCommonProps } from '../utility/commonProps.js';
import '../IconButton/IconButton.js';
import '../TextField/TextField.js';
import '../TabNavigation/TabNavigation.js';
import '../TabNavigation/TabNavigationItem.js';
import '../Modal/Modal.js';
import '@livingdesign/tokens/dist/js/light/regular/components/ErrorMessage';
import '../SearchBox/SearchBox.js';
import '../AutoComplete/AutoComplete.js';
import '../TextArea/TextArea.js';
import '../ProgressIndicator/ProgressIndicator.js';
import '../Panel/Panel.js';
import '../Calendar/locale/wmLocale.js';
import '../Calendar/locale/stdLocale.js';
import '../Calendar/context.js';
import 'dayjs';
import '../Calendar/RenderDays.js';
import '../Calendar/RenderWeeks.js';
import '@livingdesign/icons';
import '../components/Popover/Popover.js';
import '../components/InfoUnderline/InfoUnderline.js';
import '../CardActionArea/CardActionArea.js';
import '../HierarchySelector/HierarchySelector.js';
import '../Pagination/Pagination.js';
import '../DisplayTag/DisplayTag.js';
import '../PageHeader/PageHeader.js';
import '../Table/Table.js';
import '../Tooltip/Tooltip.js';
import '../ValueChange/ValueChange.js';
import '../OverlayHeader/OverlayHeader.js';
import styles from './Checkbox.module.scss.js';
import { invariant } from '../utility/invariant.js';

var _excluded = ["a11yLabelledBy", "checkboxProps", "checked", "className", "disabled", "indeterminate", "id", "label", "name", "onChange", "value", "helperText"],
  _excluded2 = ["className"];
/**
 * Checkboxes are selectable options presented in a group. A user may select any number of Checkboxes, including all or none.
 *
 * - {@link https://livingdesign.walmart.com/components/checkbox/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/checkbox/react/ React documentation}
 *
 * ### Content
 *
 * #### Label
 *
 * The Checkbox must include a label describing the option being selected. Provide a label through the `label` prop.
 *
 * Alternatively, a separate element can be used to label the Checkbox. In this case, an `a11yLabelledBy` that references the labelling component's `id` must be provided.
 *
 * (Note: `label` and `a11yLabelledBy` props are mutually exclusive.)
 *
 * ```tsx
 * <Checkbox
 *   label="My checkbox"
 *   onChange={(event) => console.log(event)}
 * />
 * ```
 *
 * *OR*
 *
 * ```tsx
 * <Checkbox
 *   a11yLabelledBy="custom-label-id"
 *   onChange={(event) => console.log(event)}
 * >
 * <span id="custom-label-id">My checkbox</span>
 * ```
 *
 * #### HTMLInputElement attributes
 *
 * The Checkbox returns an `<input type="checkbox">` element. It accepts `name` and `value` props which will be forwarded to the `input` element.
 *
 * ### Features
 *
 * #### States
 *
 * The Checkbox's states can be managed through the following optional props: `checked`, `indeterminate` and `disabled`.
 *
 * ```tsx
 * import * as React from "react";
 * import { Checkbox } from "@walmart-web/livingdesign-components";
 *
 * export default () => {
 *   return (
 *     <>
 *       <Checkbox
 *         checked={checked}
 *         label="apple"
 *         onChange={(e) => console.log(e.target.value)}
 *       />
 *       <Checkbox
 *         disabled
 *         label="banana"
 *         onChange={(e) => console.log(e.target.value)}
 *       />
 *       <Checkbox
 *         indeterminate
 *         label="orange"
 *         onChange={(e) => console.log(e.target.value)}
 *       />
 *     </>
 *   );
 * }
 * ```
 *
 * ### Interaction
 *
 * The Checkbox requires a callback to be provided to the `onChange` prop to handle interaction with checkbox.
 *
 * ```tsx
 * export default () => {
 *   const [checked, setChecked] = React.useState(false);
 *
 *  return <Checkbox checked={checked} onChange={() => setChecked(x => !x)} />
 * ```
 * The helper text for checkbox.
 * helperText?: React.ReactNode;
 */

var Checkbox = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _applyCommonProps = applyCommonProps(props, Checkbox),
    a11yLabelledBy = _applyCommonProps.a11yLabelledBy,
    _applyCommonProps$che = _applyCommonProps.checkboxProps,
    checkboxProps = _applyCommonProps$che === void 0 ? {} : _applyCommonProps$che,
    _applyCommonProps$che2 = _applyCommonProps.checked,
    checked = _applyCommonProps$che2 === void 0 ? false : _applyCommonProps$che2,
    className = _applyCommonProps.className,
    _applyCommonProps$dis = _applyCommonProps.disabled,
    disabled = _applyCommonProps$dis === void 0 ? false : _applyCommonProps$dis,
    _applyCommonProps$ind = _applyCommonProps.indeterminate,
    indeterminate = _applyCommonProps$ind === void 0 ? false : _applyCommonProps$ind,
    initialId = _applyCommonProps.id,
    label = _applyCommonProps.label,
    name = _applyCommonProps.name,
    onChange = _applyCommonProps.onChange,
    value = _applyCommonProps.value,
    helperText = _applyCommonProps.helperText,
    rest = _objectWithoutProperties(_applyCommonProps, _excluded);
  var labelCount = (label ? 1 : 0) + (a11yLabelledBy ? 1 : 0) === 1;
  invariant(labelCount, '`Checkbox` accessibility violation. `Checkbox` requires a `label` OR an `a11yLabelledBy`.');
  var checkboxClassName = checkboxProps.className,
    checkboxRest = _objectWithoutProperties(checkboxProps, _excluded2);
  var id = $b5e257d569688ac6$export$619500959fc48b26(initialId);
  var inputRef = React.useRef(null);
  React.useImperativeHandle(ref, function () {
    // istanbul ignore next
    return inputRef.current;
  });
  React.useEffect(function () {
    // istanbul ignore next
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("label", _extends({
    className: classNames(styles.label, checked && !indeterminate && styles.checked, disabled && styles.disabled, indeterminate && styles.indeterminate, className),
    htmlFor: id
  }, rest), /*#__PURE__*/React.createElement("input", _extends({
    checked: checked,
    className: classNames(styles.input, checkboxClassName),
    disabled: disabled,
    id: id,
    name: name,
    onChange: onChange,
    ref: inputRef,
    type: "checkbox",
    "data-test-cy": "checkbox-selector-".concat(id)
  }, a11yLabelledBy && {
    'aria-labelledby': a11yLabelledBy
  }, value && {
    value: value
  }, checkboxRest)), /*#__PURE__*/React.createElement("i", {
    className: styles.checkbox
  }), /*#__PURE__*/React.createElement("div", null, label && /*#__PURE__*/React.createElement("span", {
    className: styles.labelText
  }, label))), helperText != null && /*#__PURE__*/React.createElement("div", {
    className: styles.helperTextContainer
  }, /*#__PURE__*/React.createElement(FormHelperText, {
    UNSAFE_className: disabled ? styles.disableHelperText : styles.helperText,
    disabled: disabled
  }, helperText)));
});
Checkbox.displayName = 'Checkbox';

export { Checkbox };
